import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, CheckCircle, Smartphone, AlertTriangle, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '../../contexts/AuthContext';
import { generateContractText } from '../../utils/contractGenerator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const RenewalOnboarding = ({ user, onComplete }) => {
    const [step, setStep] = useState(1);
    const [hasReadBottom, setHasReadBottom] = useState(false);
    const [signature, setSignature] = useState('');
    const scrollRef = useRef(null);
    const [contractText, setContractText] = useState('');
    const { logout } = useAuth();

    // Load Renewal Data
    const renewalData = user?.staffDetails?.contractData?.renewalData;
    const personal = user?.staffDetails?.personalData || {};

    useEffect(() => {
        if (renewalData) {
            const text = generateContractText({
                // Merge current user details with renewal proposals
                staffDetails: {
                    ...user?.staffDetails,
                    contractData: {
                        ...user?.staffDetails?.contractData,
                        contractValue: renewalData.contractValue,
                        contractDuration: renewalData.contractDuration,
                        contractUnit: renewalData.contractUnit,
                        serviceDescription: renewalData.serviceDescription,
                        jobFunction: renewalData.jobFunction
                    }
                },
                accessLevel: renewalData.newRole || user.accessLevel
            });
            setContractText(text);
        }
    }, [renewalData, user]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            setHasReadBottom(true);
        }
    };

    const handleSign = () => {
        if (signature.trim().toLowerCase() === personal.fullName.toLowerCase()) {
            onComplete();
        }
    };

    if (!renewalData) return <div className="p-10 text-center text-white">Erro: Dados de renovação não encontrados.</div>;

    return (
        <div className="fixed inset-0 z-50 bg-[#09090b] flex items-center justify-center p-4">
            {/* Background Effects */}
            <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-purple-500/10 to-transparent" />

            <Card className="w-full max-w-4xl bg-black/40 border-white/10 backdrop-blur-xl shadow-2xl relative z-10 overflow-hidden">
                <div className="md:flex h-[600px]">
                    {/* Sidebar Steps */}
                    <div className="w-full md:w-64 bg-white/5 border-r border-white/10 p-6 flex flex-col justify-between hidden md:flex">
                        <div className="space-y-8">
                            <div>
                                <h1 className="text-xl font-bold text-white mb-2">Renovação</h1>
                                <p className="text-xs text-gray-400">Processo de atualização contratual</p>
                            </div>

                            <div className="space-y-4">
                                <StepItem
                                    number={1}
                                    title="Boas Vindas"
                                    active={step === 1}
                                    completed={step > 1}
                                />
                                <StepItem
                                    number={2}
                                    title="Novo Contrato"
                                    active={step === 2}
                                    completed={step > 2}
                                />
                                <StepItem
                                    number={3}
                                    title="Assinatura"
                                    active={step === 3}
                                    completed={step > 3}
                                />
                            </div>
                        </div>

                        <div className="text-xs text-gray-500">
                            Protocolo: {Date.now().toString(36).toUpperCase()}
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 p-8 flex flex-col relative">
                        {/* Logout Button */}
                        <Button
                            variant="ghost"
                            className="absolute top-4 right-4 text-gray-400 hover:text-white hover:bg-white/10"
                            onClick={logout}
                        >
                            <X className="w-4 h-4 mr-2" />
                            Sair
                        </Button>

                        <div className="flex-1 overflow-y-auto pr-2">
                            <AnimatePresence mode="wait">
                                {step === 1 && (
                                    <motion.div
                                        key="step1"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="h-full flex flex-col justify-center items-center text-center space-y-6"
                                    >
                                        <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mb-4 ring-1 ring-purple-500/40">
                                            <FileText className="w-10 h-10 text-purple-400" />
                                        </div>
                                        <h2 className="text-3xl font-bold text-white">Proposta de Renovação</h2>
                                        <p className="text-gray-400 max-w-lg">
                                            Olá, {personal.fullName.split(' ')[0]}! A administração enviou uma proposta de renovação do seu contrato.
                                        </p>

                                        <div className="bg-white/5 p-6 rounded-xl border border-white/10 w-full max-w-md space-y-3">
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-gray-400">Novo Valor:</span>
                                                <span className="text-green-400 font-bold font-mono">R$ {renewalData.contractValue}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-gray-400">Nova Duração:</span>
                                                <span className="text-blue-400 font-bold">{renewalData.contractDuration} {renewalData.contractUnit === 'days' ? 'Dias' : 'Meses'}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-gray-400">Cargo:</span>
                                                <span className="text-purple-400 font-bold">{renewalData.newRole || user.accessLevel}</span>
                                            </div>
                                        </div>

                                        <Button
                                            onClick={() => setStep(2)}
                                            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 rounded-xl text-lg mt-4 shadow-lg shadow-purple-900/40"
                                        >
                                            Revisar Contrato
                                            <ArrowRight className="w-5 h-5 ml-2" />
                                        </Button>
                                    </motion.div>
                                )}

                                {step === 2 && (
                                    <motion.div
                                        key="step2"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="h-full flex flex-col"
                                    >
                                        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                                            <FileText className="w-6 h-6 text-purple-400" />
                                            Termos de Renovação
                                        </h2>

                                        <div
                                            className="flex-1 bg-white/5 rounded-xl border border-white/10 p-6 overflow-y-auto mb-6 font-mono text-sm text-gray-300 leading-relaxed custom-scrollbar"
                                            onScroll={handleScroll}
                                            ref={scrollRef}
                                            style={{ maxHeight: '400px' }}
                                        >
                                            <pre className="whitespace-pre-wrap font-sans">{contractText}</pre>
                                            <div className="h-10" />
                                        </div>

                                        <div className="flex justify-between items-center pt-4 border-t border-white/10">
                                            <Button variant="ghost" className="text-gray-400" onClick={() => setStep(1)}>
                                                Voltar
                                            </Button>
                                            <Button
                                                onClick={() => setStep(3)}
                                                disabled={!hasReadBottom}
                                                className={`px-8 ${hasReadBottom ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-700 cursor-not-allowed'} text-white transition-all`}
                                            >
                                                {hasReadBottom ? 'Concordar e Prosseguir' : 'Leia até o final'}
                                            </Button>
                                        </div>
                                    </motion.div>
                                )}

                                {step === 3 && (
                                    <motion.div
                                        key="step3"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="h-full flex flex-col justify-center items-center text-center space-y-8"
                                    >
                                        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center ring-1 ring-green-500/40">
                                            <CheckCircle className="w-8 h-8 text-green-400" />
                                        </div>

                                        <div>
                                            <h2 className="text-2xl font-bold text-white mb-2">Assinatura Digital</h2>
                                            <p className="text-gray-400 max-w-md mx-auto">
                                                Para confirmar a renovação, digite seu nome completo exatamente como consta no cadastro.
                                            </p>
                                        </div>

                                        <div className="w-full max-w-sm space-y-2 text-left">
                                            <label className="text-xs text-gray-500 uppercase font-bold ml-1">Para confirmar digite:</label>
                                            <div className="bg-black/40 p-3 rounded border border-white/10 text-gray-400 font-mono text-sm select-all">
                                                {personal.fullName}
                                            </div>
                                            <input
                                                type="text"
                                                value={signature}
                                                onChange={(e) => setSignature(e.target.value)}
                                                placeholder="Digite seu nome completo"
                                                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 mt-2"
                                            />
                                        </div>

                                        <div className="flex items-center gap-4 pt-4">
                                            <Button variant="ghost" className="text-gray-400" onClick={() => setStep(2)}>
                                                Revisar
                                            </Button>
                                            <Button
                                                onClick={handleSign}
                                                disabled={signature.trim().toLowerCase() !== personal.fullName.toLowerCase()}
                                                className="bg-green-600 hover:bg-green-700 text-white px-8 h-12 rounded-xl shadow-lg shadow-green-900/20 transition-all font-bold tracking-wide"
                                            >
                                                Assinar Renovação
                                            </Button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const StepItem = ({ number, title, active, completed }) => (
    <div className={`flex items-center gap-4 ${active ? 'opacity-100' : 'opacity-40'}`}>
        <div className={`
            w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all
            ${active ? 'bg-purple-500 text-white ring-4 ring-purple-500/20' :
                completed ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-400'}
        `}>
            {completed ? <CheckCircle className="w-4 h-4" /> : number}
        </div>
        <span className={`font-medium ${active ? 'text-white' : 'text-gray-400'}`}>{title}</span>
    </div>
);

export default RenewalOnboarding;
